/* eslint-disable @typescript-eslint/no-namespace */
export namespace AuthorizeAcceptSuite {
  export class PaymentProcessor {
    secureData: SecureData;

    constructor(secureData: SecureData) {
      this.secureData = secureData;
    }

    async getResponse(): Promise<DispatchDataResponse> {
      const securedDataResp = await new Promise<DispatchDataResponse>(
        (resolve) => {
          // Simulate calling a method from an external library (like window.Accept.dispatchData)
          window.Accept.dispatchData(
            this.secureData,
            (response: DispatchDataResponse) => {
              resolve(response);
            },
          );
        },
      );

      return securedDataResp;
    }
  }

  export class CardData {
    cardNumber: string;
    month: string;
    year: string;
    cardCode?: string;
    zip?: string;
    fullName?: string;

    constructor(
      cardNumber: string,
      month: string,
      year: string,
      cardCode?: string,
      zip?: string,
      fullName?: string,
    ) {
      this.cardNumber = cardNumber;
      this.month = month;
      this.year = year;
      this.cardCode = cardCode;
      this.zip = zip;
      this.fullName = fullName;
    }
  }

  export class AuthData {
    clientKey: string;
    apiLoginID: string;

    constructor(clientKey: string, apiLoginID: string) {
      this.clientKey = clientKey;
      this.apiLoginID = apiLoginID;
    }
  }

  /**
   * Accept.js rejects card holder names longer than this with
   * E_WC_17 "Please provide valid card holder name." before any request is sent.
   */
  export const MAX_CARD_HOLDER_NAME_LENGTH = 64;

  /**
   * Trims the card holder name to what Accept.js accepts. Returns undefined for
   * an empty name so Accept.js skips the field instead of validating it.
   */
  export const sanitizeCardHolderName = (
    fullName: string | undefined,
  ): string | undefined => {
    const trimmed = fullName?.replace(/\s+/g, " ").trim();
    if (!trimmed) {
      return undefined;
    }
    return trimmed.slice(0, MAX_CARD_HOLDER_NAME_LENGTH).trim();
  };

  export class SecureData {
    authData: AuthData;
    cardData: CardData;

    constructor(authData: AuthData, cardData: CardData) {
      this.authData = authData;
      // Accept.js reads `.length` on any card data key that is present, so an
      // empty name must be omitted rather than set to undefined or "".
      const { fullName, ...rest } = cardData;
      const sanitizedFullName = sanitizeCardHolderName(fullName);
      this.cardData = sanitizedFullName
        ? { ...rest, fullName: sanitizedFullName }
        : rest;
    }
  }

  export interface AcceptMessage {
    code: string;
    description: string;
  }

  export type AcceptSuiteErrorMessage = { code: string; text: string };

  export type DispatchDataResponse = {
    opaqueData: {
      dataDescriptor: string;
      dataValue: string;
    };
    messages: {
      resultCode: "Ok" | "Error";
      message: AcceptSuiteErrorMessage[];
    };
  };

  export type DispatchDataFn = (
    secureData: SecureData,
    responseHandler: (response: DispatchDataResponse) => void,
  ) => void;
}
